/**
 * LinkedIn Rejection Page Component
 *
 * Displayed when a user doesn't meet the minimum LinkedIn connections requirement
 * for accessing a survey.
 */

import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Linkedin, Users, XCircle, ArrowLeft, AlertCircle } from 'lucide-react';

const LinkedInRejectionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const shareId = searchParams.get('share_id');
  const connectionsCount = parseInt(searchParams.get('connections') || '0', 10);
  const requiredConnections = parseInt(searchParams.get('required') || '0', 10);
  const isDuplicate = searchParams.get('duplicate') === 'true';

  const shortfall = Math.max(0, requiredConnections - connectionsCount);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isDuplicate ? 'Already Verified' : 'Not Eligible'}
          </h1>
          <p className="text-gray-600">
            {isDuplicate
              ? "You've already been verified for this survey."
              : "Unfortunately, you don't meet the requirements for this survey."}
          </p>
        </div>

        {/* Connection Stats */}
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-gray-900">LinkedIn Connections</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">Your Connections</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">
                {connectionsCount.toLocaleString()}
              </p>
            </div>

            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">Required</span>
              </div>
              <p className="text-2xl font-bold text-red-600">
                {requiredConnections.toLocaleString()}
              </p>
            </div>
          </div>

          {!isDuplicate && shortfall > 0 && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                You need <strong>{shortfall.toLocaleString()}</strong> more connections to participate in this survey.
              </p>
            </div>
          )}
        </div>

        {/* Explanation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-blue-900 mb-2">Why is this required?</h3>
          <p className="text-sm text-blue-800">
            The survey creator has set a minimum LinkedIn connection requirement to ensure
            respondents have established professional networks. This helps maintain the
            quality and relevance of survey responses.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          {shareId && (
            <p className="text-sm text-gray-500 text-center">
              If you believe this is an error, please contact the survey creator.
            </p>
          )}
          <button
            onClick={() => navigate('/')}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkedInRejectionPage;
